import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import ChartCard from '../Dashboard/ChartCard';
import { TemporalAnalysisDto } from '../../services/learning/QuizReportService';

interface TemporalEvolutionChartProps {
  data: TemporalAnalysisDto | null;
  title?: string;
  subtitle?: string;
}

const TemporalEvolutionChart: React.FC<TemporalEvolutionChartProps> = ({ 
  data, 
  title = 'Evolução Temporal',
  subtitle 
}) => {
  if (!data || !data.dataPoints || data.dataPoints.length === 0) {
    return (
      <ChartCard title={title} subtitle={subtitle}>
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          Nenhum dado disponível para exibir
        </div>
      </ChartCard>
    );
  }

  const chartData = data.dataPoints.map(point => ({
    data: point.date,
    trainees: point.traineesCount,
    mediaNotas: parseFloat(point.averageScore.toFixed(1)),
    taxaAprovacao: parseFloat(point.approvalRate.toFixed(1)),
    tentativasCompletas: point.completedAttempts,
  }));

  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    } catch {
      return dateStr;
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: 'rgba(44, 62, 80, 0.95)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '8px',
          padding: '12px',
          color: '#fff',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>{formatDate(label)}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} style={{ margin: '4px 0', color: entry.color }}>
              {entry.name}: {entry.value}{entry.name.includes('Taxa') || entry.name.includes('Média') ? '%' : ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <ChartCard title={title} subtitle={subtitle}>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart 
          data={chartData} 
          margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(0, 0, 0, 0.1)" />
          <XAxis 
            dataKey="data" 
            tick={{ fill: '#666', fontSize: 12 }}
            angle={-45}
            textAnchor="end"
            height={80}
            tickFormatter={formatDate}
          />
          <YAxis 
            yAxisId="left"
            tick={{ fill: '#666' }}
            label={{ value: 'Percentual (%)', angle: -90, position: 'insideLeft' }}
          />
          <YAxis 
            yAxisId="right"
            orientation="right"
            tick={{ fill: '#666' }}
            label={{ value: 'Quantidade', angle: 90, position: 'insideRight' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Line 
            yAxisId="left"
            type="monotone" 
            dataKey="taxaAprovacao" 
            stroke="#2ecc71" 
            strokeWidth={2}
            name="Taxa de Aprovação (%)"
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
          <Line 
            yAxisId="left"
            type="monotone" 
            dataKey="mediaNotas" 
            stroke="#3498db" 
            strokeWidth={2}
            name="Média de Notas (%)"
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
          <Line 
            yAxisId="right"
            type="monotone" 
            dataKey="trainees" 
            stroke="#9b59b6" 
            strokeWidth={2}
            name="Número de Trainees"
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
          <Line 
            yAxisId="right"
            type="monotone" 
            dataKey="tentativasCompletas" 
            stroke="#f39c12" 
            strokeWidth={2}
            name="Tentativas Completas"
            dot={{ r: 4 }}
            activeDot={{ r: 6 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

export default TemporalEvolutionChart;

